import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useBrand } from '@/hooks/useBrand'

// Ordered by specificity — longer paths first so /settings/profile wins over /settings
const ROUTE_TITLES: Array<[string, string]> = [
  // Settings sub-routes
  ['/settings/profile', 'Profile'],
  ['/settings/password', 'Password'],
  ['/settings/notifications', 'Notifications'],
  ['/settings/voice', 'Voice'],
  ['/settings', 'Settings'],

  // Platform admin
  ['/admin/organizations', 'Organizations'],
  ['/admin/voice', 'Voice Management'],
  ['/admin/affiliates', 'Affiliates'],
  ['/admin/demo-leads', 'Demo Leads'],
  ['/admin/demo-dnc', 'Demo DNC'],
  ['/admin', 'The Castle'],

  // Onboarding
  ['/onboarding/step/1', 'Create Account'],
  ['/onboarding/step/2', 'Setup'],
  ['/onboarding/step/3', 'Launch'],
  ['/onboarding', 'Onboarding'],

  // Team subsections
  ['/team/profile', 'Team Profile'],
  ['/team', 'Team'],

  // Main sections
  ['/calls', 'Calls'],
  ['/chats', 'Live Chats'],
  ['/conversations', 'Chat History'],
  ['/follow-up-queue', 'Follow-Up Queue'],
  ['/bookings', 'Bookings Calendar'],
  ['/messages', 'Team Messages'],
  ['/agents', 'AI Agents'],
  ['/analytics', 'Analytics'],
  ['/widget', 'Chat Widget'],
  ['/integrations', 'Integrations'],
  ['/form-connections', 'Form Connections'],
  ['/sms', 'SMS'],
  ['/demo', 'Demo'],
  ['/try', 'Demo'],
  ['/affiliate/apply', 'Affiliate Application'],
  ['/affiliate', 'Affiliate'],

  // Home
  ['/', 'Dashboard'],
]

function titleForPath(pathname: string, appSuffix: string): string {
  for (const [prefix, label] of ROUTE_TITLES) {
    if (prefix === '/' ? pathname === '/' : pathname === prefix || pathname.startsWith(prefix + '/')) {
      return `${label} · ${appSuffix}`
    }
  }
  return appSuffix
}

/**
 * Updates document.title based on the current route so the browser tab
 * distinguishes the app from the marketing site and shows the current section.
 * The suffix is the resolved white-label brand name.
 */
export function useRouteDocumentTitle() {
  const { pathname } = useLocation()
  const brand = useBrand()
  useEffect(() => {
    document.title = titleForPath(pathname, brand.appName)
  }, [pathname, brand.appName])
}
